import React, { useState } from "react";

import PageHeader from "../components/layout/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import PublicFormCapabilityNotice from "../components/settings/public-forms/PublicFormCapabilityNotice";
import FormContextSelector from "../components/settings/public-forms/FormContextSelector";
import PublicFormEditor from "../components/settings/public-forms/PublicFormEditor";
import PublicFormEmbeddingPanel from "../components/settings/public-forms/PublicFormEmbeddingPanel";
import PublicFormPlacementDialog from "../components/settings/public-forms/PublicFormPlacementDialog";
import PublicFormPlacementsList from "../components/settings/public-forms/PublicFormPlacementsList";
import PublicFormsList from "../components/settings/public-forms/PublicFormsList";
import usePublicFormsManager from "../components/settings/public-forms/usePublicFormsManager";
import { useAuth } from "../lib/auth-context";

export default function FormsPage() {
  const { tenant, capabilities } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const {
    loading,
    forms,
    selectedFormId,
    selectedForm,
    placements,
    createName,
    createKind,
    savingForm,
    savingPlacement,
    placementDialogOpen,
    placementDialogMode,
    placementDraft,
    publicFormsEnabled,
    createDisabledReason,
    setCreateName,
    setCreateKind,
    setSelectedForm,
    setPlacementDraft,
    handleCreateForm,
    handleSelectForm,
    handleSaveForm,
    openPlacementCreateDialog,
    openPlacementEditDialog,
    handleSavePlacement,
    closePlacementDialog,
  } = usePublicFormsManager({ capabilities });

  const handleCopy = (text) => {
    navigator.clipboard?.writeText?.(text);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Formulare"
        description={`Verwalten Sie Formulare, Einsatzorte und Einbettungen für ${tenant?.name || "Ihren Mandanten"}.`}
      />

      <PublicFormCapabilityNotice capabilities={capabilities} formCount={forms.length} />

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="forms-page-tabs">
        <TabsList className="justify-start">
          <TabsTrigger value="overview" data-testid="forms-page-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="editor" data-testid="forms-page-tab-editor">Formular-Editor</TabsTrigger>
          <TabsTrigger value="placements" data-testid="forms-page-tab-placements">Einsatzorte</TabsTrigger>
          <TabsTrigger value="embedding" data-testid="forms-page-tab-embedding">Einbettung</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card data-testid="forms-overview-quick-actions">
            <CardHeader className="pb-3">
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Wechseln Sie direkt in den passenden Arbeitsbereich für das ausgewählte Formular.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Ausgewähltes Formular: {selectedForm?.name || "Kein Formular ausgewählt"}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="button" onClick={() => setActiveTab("editor")} disabled={!selectedForm}>
                  Bearbeiten
                </Button>
                <Button type="button" variant="outline" onClick={() => setActiveTab("placements")} disabled={!selectedForm}>
                  Einsatzorte
                </Button>
                <Button type="button" variant="outline" onClick={() => setActiveTab("embedding")} disabled={!selectedForm}>
                  Einbettung
                </Button>
              </div>
            </CardContent>
          </Card>

          <div>
            <PublicFormsList
              forms={forms}
              selectedFormId={selectedFormId}
              onSelect={handleSelectForm}
              createName={createName}
              onCreateNameChange={setCreateName}
              createKind={createKind}
              onCreateKindChange={setCreateKind}
              onCreate={handleCreateForm}
              createDisabled={Boolean(createDisabledReason)}
              createDisabledReason={createDisabledReason}
            />
          </div>
        </TabsContent>

        <TabsContent value="editor" className="mt-4">
          <div className="space-y-4">
            <FormContextSelector
              forms={forms}
              selectedFormId={selectedFormId}
              selectedForm={selectedForm}
              onSelect={handleSelectForm}
              selectId="forms-editor-context-selector"
              description="Dieses Formular wird aktuell bearbeitet."
            />
            {selectedForm ? (
              <PublicFormEditor
                form={selectedForm}
                onChange={setSelectedForm}
                onSave={handleSaveForm}
                saving={savingForm}
                baseUrl={window.location.origin}
                canUseEmailTracking={Boolean(capabilities?.email_tracking_allowed)}
                canUseMarketingConsent={Boolean(capabilities?.marketing_consent_allowed)}
                onCopy={(text) => handleCopy(text)}
              />
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="placements" className="mt-4">
          <div className="space-y-4">
            <FormContextSelector
              forms={forms}
              selectedFormId={selectedFormId}
              selectedForm={selectedForm}
              onSelect={handleSelectForm}
              selectId="forms-placements-context-selector"
              description="Diese Einsatzorte gehören zum ausgewählten Formular."
            />
            {publicFormsEnabled && selectedForm ? (
              <PublicFormPlacementsList
                placements={placements}
                onCreate={openPlacementCreateDialog}
                onEdit={openPlacementEditDialog}
                onCopyPlacementKey={() => {}}
                onCopySnippet={() => {}}
                widgetEmbedEnabled={Boolean(capabilities?.widget_embed_enabled)}
                formName={selectedForm.name}
                formKind={selectedForm.form_kind}
                baseUrl={window.location.origin}
                variant="table"
                showEmbeddingDetails={false}
                showTechnicalDetails={false}
              />
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="embedding" className="mt-4">
          <div className="space-y-4">
            <FormContextSelector
              forms={forms}
              selectedFormId={selectedFormId}
              selectedForm={selectedForm}
              onSelect={handleSelectForm}
              selectId="forms-embedding-context-selector"
              description="Diese Einbettungsdaten gehören zum ausgewählten Formular."
            />
            {selectedForm ? (
              <PublicFormEmbeddingPanel
                baseUrl={window.location.origin}
                form={selectedForm}
                placements={placements}
                widgetEmbedEnabled={Boolean(capabilities?.widget_embed_enabled)}
                onCopy={(text) => handleCopy(text)}
              />
            ) : null}
          </div>
        </TabsContent>
      </Tabs>

      <PublicFormPlacementDialog
        open={placementDialogOpen}
        mode={placementDialogMode}
        value={placementDraft}
        saving={savingPlacement}
        onChange={setPlacementDraft}
        onSubmit={handleSavePlacement}
        onClose={closePlacementDialog}
      />
    </div>
  );
}
